import React from 'react'
import PropTypes from 'prop-types'
import {Text, Link, UnorderedList, OrderedList, ListItem} from '@chakra-ui/react'

const UNORDERED_LIST_REGEX = /^\s*[-*•]\s+(.*)$/
const ORDERED_LIST_REGEX = /^\s*(\d+)[.)]\s+(.*)$/
const INLINE_TOKEN_REGEX =
    /(\*\*[^*]+\*\*|https?:\/\/[^\s]+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g

/**
 * Strips trailing sentence punctuation from URLs or emails
 * while preserving balanced parentheses.
 *
 * @param {string} raw - The raw URL or email string
 * @returns {{clean: string, trailing: string}}
 */
const splitTrailingPunctuation = (raw) => {
    let clean = raw
    let trailing = ''

    while (clean.length > 0) {
        const lastChar = clean[clean.length - 1]
        if (['.', ',', ';', ':', '!', '?'].includes(lastChar)) {
            trailing = lastChar + trailing
            clean = clean.slice(0, -1)
        } else if (lastChar === ')') {
            const openCount = clean.split('(').length - 1
            const closeCount = clean.split(')').length - 1
            if (closeCount > openCount) {
                trailing = lastChar + trailing
                clean = clean.slice(0, -1)
            } else {
                break
            }
        } else {
            break
        }
    }

    return {clean, trailing}
}

/**
 * Parses inline string into React nodes supporting links, emails, and bold text.
 *
 * @param {string} text - Plain text line
 * @param {object} styles - Theme styles
 * @returns {React.ReactNode[]}
 */
const renderInlineContent = (text, styles) => {
    const tokens = text.split(INLINE_TOKEN_REGEX)

    return tokens.map((token, index) => {
        if (!token) {
            return null
        }

        // Bold formatting: **text**
        if (token.startsWith('**') && token.endsWith('**') && token.length > 4) {
            const boldText = token.slice(2, -2)
            return (
                <Text as="strong" fontWeight="bold" key={index}>
                    {boldText}
                </Text>
            )
        }

        // Web URLs: https?://
        if (/^https?:\/\//i.test(token)) {
            const {clean, trailing} = splitTrailingPunctuation(token)
            return (
                <React.Fragment key={index}>
                    <Link href={clean} isExternal {...styles.reviewCommentLink}>
                        {clean}
                    </Link>
                    {trailing}
                </React.Fragment>
            )
        }

        // Email addresses: user@domain.tld
        if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/i.test(token)) {
            const {clean, trailing} = splitTrailingPunctuation(token)
            return (
                <React.Fragment key={index}>
                    <Link href={`mailto:${clean}`} {...styles.reviewCommentLink}>
                        {clean}
                    </Link>
                    {trailing}
                </React.Fragment>
            )
        }

        return token
    })
}

/**
 * Pure presentation component for parsing and rendering customer service replies.
 * Transforms plain text into paragraphs, unordered/ordered lists, and links.
 */
const FormattedResponse = ({text, styles = {}}) => {
    if (!text || !text.trim()) {
        return null
    }

    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    const rawLines = normalized.split('\n')

    const blocks = []
    let currentBlock = null

    for (let i = 0; i < rawLines.length; i++) {
        const line = rawLines[i]
        const unorderedMatch = line.match(UNORDERED_LIST_REGEX)
        const orderedMatch = line.match(ORDERED_LIST_REGEX)

        if (unorderedMatch) {
            if (currentBlock?.type === 'unordered-list') {
                currentBlock.items.push(unorderedMatch[1])
            } else {
                if (currentBlock) {
                    blocks.push(currentBlock)
                }
                currentBlock = {type: 'unordered-list', items: [unorderedMatch[1]]}
            }
        } else if (orderedMatch) {
            if (currentBlock?.type === 'ordered-list') {
                currentBlock.items.push(orderedMatch[2])
            } else {
                if (currentBlock) {
                    blocks.push(currentBlock)
                }
                currentBlock = {type: 'ordered-list', items: [orderedMatch[2]]}
            }
        } else if (line.trim() === '') {
            if (currentBlock) {
                blocks.push(currentBlock)
                currentBlock = null
            }
        } else {
            if (currentBlock?.type === 'paragraph') {
                currentBlock.lines.push(line)
            } else {
                if (currentBlock) {
                    blocks.push(currentBlock)
                }
                currentBlock = {type: 'paragraph', lines: [line]}
            }
        }
    }

    if (currentBlock) {
        blocks.push(currentBlock)
    }

    return (
        <>
            {blocks.map((block, blockIndex) => {
                if (block.type === 'unordered-list') {
                    return (
                        <UnorderedList {...styles.reviewCommentList} key={blockIndex}>
                            {block.items.map((item, itemIndex) => (
                                <ListItem {...styles.reviewCommentListItem} key={itemIndex}>
                                    {renderInlineContent(item, styles)}
                                </ListItem>
                            ))}
                        </UnorderedList>
                    )
                }

                if (block.type === 'ordered-list') {
                    return (
                        <OrderedList {...styles.reviewCommentOrderedList} key={blockIndex}>
                            {block.items.map((item, itemIndex) => (
                                <ListItem {...styles.reviewCommentListItem} key={itemIndex}>
                                    {renderInlineContent(item, styles)}
                                </ListItem>
                            ))}
                        </OrderedList>
                    )
                }

                return (
                    <Text as="p" {...styles.reviewCommentParagraph} key={blockIndex}>
                        {block.lines.map((line, lineIndex) => (
                            <React.Fragment key={lineIndex}>
                                {lineIndex > 0 && <br />}
                                {renderInlineContent(line, styles)}
                            </React.Fragment>
                        ))}
                    </Text>
                )
            })}
        </>
    )
}

FormattedResponse.propTypes = {
    text: PropTypes.string,
    styles: PropTypes.object
}

export default FormattedResponse
